import { useState, useEffect, useCallback, useRef } from "react";
import { isRawFormat } from "@beleef/editor-shot";
import { useRawImageConversion } from "apis/public";
import {
  isRevisionCommentPending,
  resolveImageRevisionUrl,
} from "utils/prepareMarketing";

const sortCommentsNewest = (a, b) =>
  new Date(b.createdAt || 0) - new Date(a.createdAt || 0);

/**
 * For extras batches: point each image at the revision an editor flagged in their
 * review feedback (commentedRawPhotos), not the active/default revision.
 * Runs during state setup; useRawImageConversion then renders that S3 URL to JPG.
 */
export const applyEditorReviewRevisionUrls = (
  images,
  commentedRawPhotos = [],
) => {
  const revisionIdByImage = new Map();
  for (const entry of commentedRawPhotos) {
    const revisionComments = (entry.comments || []).filter(
      (c) => c.revisionId != null,
    );
    const pending = revisionComments
      .filter(isRevisionCommentPending)
      .sort(sortCommentsNewest);
    const display =
      pending[0] || [...revisionComments].sort(sortCommentsNewest)[0];
    if (display?.revisionId != null) {
      revisionIdByImage.set(entry.imageId, String(display.revisionId));
    }
  }

  return (images || []).map((image) => {
    const revisionId = revisionIdByImage.get(image.id);
    if (!revisionId) return image;
    const revisionUrl = resolveImageRevisionUrl(image, revisionId);
    if (!revisionUrl) return image;
    return {
      ...image,
      url: revisionUrl,
      originalUrl: image.originalUrl || image.url,
    };
  });
};

const MAX_CONVERSION_ATTEMPTS = 3;
const BATCH_DELAY_MS = 1000;
const BATCH_SIZE = 3;

const sameId = (a, b) => String(a) === String(b);

/**
 * Batch converts RAW images (CR3, DNG, ARW, etc.) to viewable JPGs.
 *
 * Returns:
 *   - convertSingle({ imageUrl, imageId }) — call after a single upload
 *   - retryConversion(imageId) — manually retry a failed image
 *   - isConverting — true while any conversions are in flight
 *
 * Each conversion retries up to 3× with backoff (1s, 2s, 3s). On final
 * failure the image is marked `conversionFailed: true` so the UI can show
 * a Retry button.
 */
const useRawImageRenderer = ({
  images,
  setImages,
  enabled = false,
  filterFn,
}) => {
  const [isConverting, setIsConverting] = useState(false);

  const mutation = useRawImageConversion();
  const mutationRef = useRef(mutation);
  mutationRef.current = mutation;

  const setImagesRef = useRef(setImages);
  setImagesRef.current = setImages;

  const imagesRef = useRef(images);
  imagesRef.current = images;

  const processedIdsRef = useRef(new Set());
  const processingIdsRef = useRef(new Set());

  const attemptConversion = useCallback(async ({ imageUrl, imageId }) => {
    const id = String(imageId);
    if (processingIdsRef.current.has(id)) return;
    processingIdsRef.current.add(id);
    setIsConverting(true);

    let data = null;
    let lastError = null;
    for (let attempt = 1; attempt <= MAX_CONVERSION_ATTEMPTS; attempt++) {
      try {
        data = await mutationRef.current.mutateAsync({
          imageUrl,
          imageId,
          revisionId: null,
        });
        break;
      } catch (err) {
        lastError = err;
        if (attempt < MAX_CONVERSION_ATTEMPTS) {
          await new Promise((r) => setTimeout(r, 1000 * attempt));
        }
      }
    }

    if (data?.convertedUrl) {
      setImagesRef.current((prev) =>
        prev.map((img) =>
          sameId(img.id, imageId)
            ? {
                ...img,
                originalUrl: img.originalUrl || data.originalUrl || imageUrl,
                url: data.convertedUrl,
                conversionFailed: false,
              }
            : img,
        ),
      );
    } else {
      // eslint-disable-next-line no-console
      console.error(
        `RAW conversion failed after ${MAX_CONVERSION_ATTEMPTS} attempts:`,
        lastError?.message,
      );
      setImagesRef.current((prev) =>
        prev.map((img) =>
          sameId(img.id, imageId) ? { ...img, conversionFailed: true } : img,
        ),
      );
    }

    processedIdsRef.current.add(id);
    processingIdsRef.current.delete(id);
    if (processingIdsRef.current.size === 0) setIsConverting(false);
  }, []);

  const defaultFilter = useCallback((img) => {
    const id = String(img?.id);
    if (!img?.name || !isRawFormat(img.name)) return false;
    if (processedIdsRef.current.has(id) || processingIdsRef.current.has(id)) {
      return false;
    }
    if (img.conversionFailed) return false;

    const display = img.url || img.originalUrl;
    if (!display || String(display).includes("blob:")) return false;
    // Already converted / JPEG revision — nothing to do.
    if (String(display).startsWith("data:") || !isRawFormat(display)) {
      return false;
    }
    return img.isUploaded === undefined || img.isUploaded === true;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    let stopped = false;

    const tick = async () => {
      while (!stopped) {
        const filter = filterFn || defaultFilter;
        const unprocessed = (imagesRef.current || []).filter(filter);
        if (unprocessed.length === 0) {
          await new Promise((r) => setTimeout(r, 750));
          continue;
        }

        const batch = unprocessed.slice(0, BATCH_SIZE);
        await Promise.all(
          batch.map((img) =>
            attemptConversion({
              imageUrl: img.originalUrl || img.url,
              imageId: img.id,
            }),
          ),
        );
        if (!stopped && unprocessed.length > BATCH_SIZE) {
          await new Promise((r) => setTimeout(r, BATCH_DELAY_MS));
        }
      }
    };

    tick();
    return () => {
      stopped = true;
    };
  }, [enabled, attemptConversion, filterFn, defaultFilter]);

  const convertSingle = useCallback(
    ({ imageUrl, imageId }) => {
      processedIdsRef.current.delete(String(imageId));
      attemptConversion({ imageUrl, imageId });
    },
    [attemptConversion],
  );

  const retryConversion = useCallback(
    (imageId) => {
      const img = (imagesRef.current || []).find((i) =>
        sameId(i.id, imageId),
      );
      if (!img) return;
      setImagesRef.current((prev) =>
        prev.map((i) =>
          sameId(i.id, imageId) ? { ...i, conversionFailed: false } : i,
        ),
      );
      processedIdsRef.current.delete(String(imageId));
      attemptConversion({
        imageUrl: img.originalUrl || img.url,
        imageId,
      });
    },
    [attemptConversion],
  );

  return {
    convertSingle,
    retryConversion,
    isConverting,
  };
};

export default useRawImageRenderer;
